import { z } from 'zod';

/**
 * - `.trim()` before `z.email()`, which rejects pasted addresses with surrounding spaces.
 * - `.min(1)` after the trim, so a spaces-only field reports "Required".
 */
const email = z.string().trim().min(1, 'Required').pipe(z.email('Enter a valid email address'));

const requiredString = z.string().min(1, 'Required');

export const inviteDeveloperFormSchema = z.object({ email });

/**
 * - `name` restates apps-api's rule: the client validates nothing; apps-api answers an opaque 400.
 * - The charset is narrow because apps-api derives `username` from it.
 * - `description` allows `''` because a text input always yields a string; dropped at submit.
 */
export const createServiceAccountFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Required')
    .max(64)
    .regex(/^[a-zA-Z0-9_]+$/, 'Only letters, digits and underscores'),
  description: z.union([z.literal(''), z.string().max(256)]),
});

/**
 * A vendor's test-project URL.
 *
 * - `protocol` pinned: bare `z.url()` passes `javascript:`, and the profile page renders an `href`.
 * - Exported so that render sink re-checks it: stored rows and admin edits bypass this form.
 */
export const projectUrlSchema = z.url({
  protocol: /^https?$/,
  error: 'Enter a valid URL, including https://',
});

/**
 * Vendor details as the profile page edits them; `id` is admin-only, set at creation.
 *
 * - `projectUrl` accepts `''` so the field can be cleared.
 */
export const editVendorFormSchema = z.object({
  name: requiredString.max(128),
  email,
  address: requiredString,
  projectUrl: z.union([z.literal(''), projectUrlSchema]),
});

/**
 * The optional shutdown date a vendor may attach when deprecating a component.
 *
 * - `''` allowed because a text input cannot hold `undefined`; dropped at submit.
 * - `z.iso.date()`, not a regex: impossible dates (`2027-02-31`) get an opaque 400 from apps-api.
 */
export const deprecateAppFormSchema = z.object({
  expiredOn: z.union([z.literal(''), z.iso.date('Use the YYYY-MM-DD format')]),
});
